import { useId, useState, type KeyboardEvent, type ReactElement } from 'react';
import { FormInput } from '@/components/molecules/form-input';
import { Chip } from '@/components/atoms/chip';
import { Icon } from '@/components/atoms/icon';

export type ContributeIdentifySectionProps = {
  title: string;
  aka: string[];
  onTitleChange: (value: string) => void;
  onAddAka: (value: string) => void;
  onRemoveAka: (index: number) => void;
};

export function ContributeIdentifySection({
  title,
  aka,
  onTitleChange,
  onAddAka,
  onRemoveAka,
}: ContributeIdentifySectionProps): ReactElement {
  const titleId = useId();
  const akaId = useId();
  const [akaDraft, setAkaDraft] = useState('');

  const submitAka = (): void => {
    const trimmed = akaDraft.trim();
    if (trimmed.length === 0) return;
    onAddAka(trimmed);
    setAkaDraft('');
  };

  const handleAkaKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitAka();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <FormInput
        id={titleId}
        label="Title"
        isRequired
        required
        autoFocus
        value={title}
        onChange={(event) => onTitleChange(event.target.value)}
      />

      <FormInput
        id={akaId}
        label="Also known as"
        value={akaDraft}
        onChange={(event) => setAkaDraft(event.target.value)}
        onKeyDown={handleAkaKeyDown}
        hint="Press Enter to add"
      />

      {aka.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Aka chips">
          {aka.map((value, index) => (
            <li key={value}>
              <Chip
                active
                onClick={() => onRemoveAka(index)}
                aria-label={`Remove "${value}"`}
              >
                <span>{value}</span>
                <Icon name="x" size={11} aria-hidden="true" />
              </Chip>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
